import React from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  Divider,
} from "@mui/material";

const DigitalGembaWalk: React.FC = () => {
  return (
    <Box sx={{ background: "#f9fafb" }}>
      {/* HEADER
      <Box sx={{ background: "#fff", borderBottom: "1px solid #eee" }}>
        <Container sx={{ py: 2, display: "flex", alignItems: "center" }}>
          <Box
            component="img"
            src="https://www.emachine.in/emachinelogo-horizontal.png"
            alt="eMachine"
            sx={{ height: 70 }}
          />
        </Container>
      </Box> */}

      {/* HERO */}
      <Box sx={{ py: 10 }}>
        <Container>
          <Grid container spacing={6} alignItems="center">
            <Grid size={{ xs: 12, md: 7 }}>
              <Chip
                label="eMachine • Digital Gemba Walk"
                sx={{
                  mb: 3,
                  background: "#e0f7fa",
                  color: "#00796b",
                }}
              />

              <Typography variant="h3" fontWeight="bold" gutterBottom>
                Turn shop floor walks into{" "}
                <Box component="span" sx={{ color: "#1976d2" }}>
                  measurable action
                </Box>
              </Typography>

              <Typography color="text.secondary" sx={{ mb: 4 }}>
                eMachine Digital Gemba Walk helps plant leaders, supervisors,
                and improvement teams standardize floor walks, capture issues in
                real time, assign actions instantly, and improve follow-through
                across the factory.
              </Typography>

              {/* <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <Button variant="contained">Request Demo</Button>
                <Button variant="outlined">Explore Features</Button>
              </Box> */}
            </Grid>

            {/* HERO CARD */}
            <Grid size={{ xs: 12, md: 5 }}>
              <Card sx={{ borderRadius: 4, boxShadow: 3 }}>
                <CardContent>
                  <Typography color="text.secondary">
                    Today's Walk Status
                  </Typography>

                  <Typography variant="h5" fontWeight="bold">
                    Assembly Line A
                  </Typography>

                  <Chip
                    label="84% Actions Closed"
                    color="success"
                    sx={{ mt: 1 }}
                  />

                  <Grid container spacing={2} sx={{ mt: 2 }}>
                    <Grid size={{ xs: 6 }}>
                      <Box
                        sx={{ p: 2, background: "#f5f5f5", borderRadius: 2 }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Observations
                        </Typography>
                        <Typography variant="h5">24</Typography>
                        <Typography variant="caption">
                          8 critical • 10 medium • 6 low
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid size={{ xs: 6 }}>
                      <Box
                        sx={{ p: 2, background: "#f5f5f5", borderRadius: 2 }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Open Actions
                        </Typography>
                        <Typography variant="h5">7</Typography>
                        <Typography variant="caption">
                          2 overdue • 5 in progress
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* FEATURES */}
      <Box sx={{ py: 10, background: "#fff" }}>
        <Container>
          <Typography align="center" color="primary">
            WHY DIGITAL GEMBA WALK
          </Typography>

          <Typography
            variant="h4"
            align="center"
            fontWeight="bold"
            sx={{ mb: 2 }}
          >
            Replace paper-based follow-up with visibility and accountability
          </Typography>

          <Typography align="center" color="text.secondary" sx={{ mb: 6 }}>
            Standardize leadership walks, improve response time, and convert
            every abnormality into a tracked improvement action.
          </Typography>

          <Grid container spacing={3}>
            {[
              {
                title: "Structured Daily Walks",
                desc: "Create standard Gemba checklists for safety, quality, production, 5S, maintenance.",
              },
              {
                title: "Mobile-First Observations",
                desc: "Capture issues, comments, photos directly from the shop floor.",
              },
              {
                title: "Action Tracking",
                desc: "Assign corrective actions instantly with priority and due dates.",
              },
              {
                title: "Escalations & Alerts",
                desc: "Trigger reminders and escalations for overdue actions.",
              },
              {
                title: "Trend Visibility",
                desc: "Identify repeated issues by area, machine, or department.",
              },
              {
                title: "Audit-Ready Records",
                desc: "Maintain digital history of observations and accountability.",
              },
            ].map((item, i) => (
              <Grid size={{ xs: 12, md: 4 }} key={i}>
                <Card sx={{ borderRadius: 4 }}>
                  <CardContent>
                    <Typography variant="h6" fontWeight="bold">
                      {item.title}
                    </Typography>
                    <Typography color="text.secondary">{item.desc}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* HOW IT WORKS */}
      <Box sx={{ py: 10 }}>
        <Container>
          <Grid container spacing={6}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="h4" fontWeight="bold" sx={{ mb: 3 }}>
                A simple workflow built for real factory conditions
              </Typography>

              {[
                "Plan standard walk routes, checkpoints, and questions.",
                "Record observations in real time using mobile.",
                "Assign corrective actions instantly.",
                "Track closure, overdue tasks, and trends.",
              ].map((step, i) => (
                <Box
                  key={i}
                  sx={{
                    display: "flex",
                    gap: 2,
                    mb: 2,
                    p: 2,
                    background: "#fff",
                    borderRadius: 3,
                    boxShadow: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      background: "#1976d2",
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {i + 1}
                  </Box>
                  <Typography>{step}</Typography>
                </Box>
              ))}
            </Grid>

            {/* IMPACT */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Card sx={{ borderRadius: 4 }}>
                <CardContent>
                  <Typography variant="h6">Impact</Typography>

                  <Grid container spacing={2} sx={{ mt: 2 }}>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="h5">120+</Typography>
                      <Typography color="text.secondary">
                        Checkpoints covered
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="h5">95%</Typography>
                      <Typography color="text.secondary">
                        Action closure rate
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="h5">Reduced</Typography>
                      <Typography color="text.secondary">
                        Response time
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="h5">Lower</Typography>
                      <Typography color="text.secondary">
                        Repeat issues
                      </Typography>
                    </Grid>
                  </Grid>

                  <Box
                    sx={{
                      mt: 3,
                      p: 2,
                      background: "#e3f2fd",
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight="bold">
                      Ideal for
                    </Typography>
                    <Typography color="text.secondary">
                      Manufacturing plants, machine shops, assembly operations,
                      maintenance teams, and quality leaders.
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* CTA */}
      {/* <Box sx={{ py: 10, background: "#1976d2", color: "#fff" }}> */}
      <Box sx={{ py: 10, background: "#fff" }}>
        <Container>
          <Grid container spacing={4} alignItems="center">
            <Grid size={{ xs: 12, md: 8 }}>
              <Typography variant="h4" fontWeight="bold">
                Improve discipline on the floor without adding reporting burden
              </Typography>
              <Typography sx={{ mt: 2 }}>
                Every walk becomes traceable, every issue gets ownership, and
                every improvement becomes measurable.
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <Button
                variant="contained"
                // background="linear-gradient(135deg, #FFC400, #ffb300)"
                fullWidth
                sx={{ mb: 2 }}
              >
                Book a Demo
              </Button>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default DigitalGembaWalk;
